import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { affiliateApi } from "../services/influancerService";
import { getAffiliateInvitationsByUsername } from "../services/affiliateService";
import { useEarnings } from "../../hooks/useEarnings";
import AIInsights from "../../components/AI_Implementation/AIInsights/AIInsights";
import AIAssistant from "../../components/AI_Implementation/AIAssistant/AIAssistant";
import { INFLUENCER_BASE } from "../constants";

const Dashboard = () => {
  const navigate = useNavigate();
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [upcomingFeature, setUpcomingFeature] = useState("");
  const [stats, setStats] = useState({
    totalLinks: 0,
    totalVisits: 0,
    totalUniqueVisits: 0,
    conversionRate: 0,
    activeCampaigns: 0,
    pendingCampaigns: 0,
    rejectedCampaigns: 0,
    totalEarnings: 0,
    dueAmount: 0,
  });
  const [loading, setLoading] = useState(true);

  const user = useSelector((state) => state.user.data);

  const {
    totalEarnings: earningsData,
    loading: earningsLoading,
    error: earningsError,
    refreshEarnings,
    isDataStale,
  } = useEarnings();

  useEffect(() => {
    if (earningsData) {
      setStats((prev) => ({
        ...prev,
        totalEarnings: earningsData.totalDue || 0,
        dueAmount: earningsData.amountUnpaid || 0,
      }));
    }
  }, [earningsData]);

  const handleRefreshEarnings = async () => {
    try {
      await refreshEarnings();
      toast.success("Earnings data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing earnings:", error);
      toast.error("Failed to refresh earnings data");
    }
  };

  const fetchAffiliateStats = useCallback(async () => {
    try {
      setLoading(true);
      const affiliateStats = await affiliateApi.getUserStats();

      let campaignStats = { activeCampaigns: 0, pendingCampaigns: 0, rejectedCampaigns: 0 };

      try {
        if (user?.username) {
          const campaignResponse = await getAffiliateInvitationsByUsername(user.username);
          if (campaignResponse.success && campaignResponse.data) {
            const counts = campaignResponse.data.counts;
            campaignStats = {
              activeCampaigns: counts.accepted || 0,
              pendingCampaigns: counts.pending || 0,
              rejectedCampaigns: counts.rejected || 0,
            };
          }
        }
      } catch (campaignError) {
        console.error("Error fetching campaign stats:", campaignError);
      }

      setStats((prev) => ({
        totalLinks: affiliateStats.totalLinks || 0,
        totalVisits: affiliateStats.totalVisits || 0,
        totalUniqueVisits: affiliateStats.totalUniqueVisits || 0,
        conversionRate: affiliateStats.conversionRate || 0,
        activeCampaigns: campaignStats.activeCampaigns || 0,
        pendingCampaigns: campaignStats.pendingCampaigns || 0,
        rejectedCampaigns: campaignStats.rejectedCampaigns || 0,
        totalEarnings: prev.totalEarnings,
        dueAmount: prev.dueAmount,
      }));
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load statistics");
      setStats((prev) => ({
        totalLinks: 0, totalVisits: 0, totalUniqueVisits: 0, conversionRate: 0,
        activeCampaigns: 0, pendingCampaigns: 0, rejectedCampaigns: 0,
        totalEarnings: prev.totalEarnings, dueAmount: prev.dueAmount,
      }));
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchAffiliateStats();
  }, [fetchAffiliateStats]);

  const quickActions = [
    {
      title: "Connect Social Media",
      description: "Link your social accounts for analytics",
      icon: "fas fa-share-alt",
      color: "text-blue-600",
      bgColor: "bg-blue-500",
      path: `${INFLUENCER_BASE}/dashboard/connect-social-media`,
    },
    {
      title: "Create Affiliate Link",
      description: "Generate new tracking links for campaigns",
      icon: "fas fa-link",
      color: "text-orange-600",
      bgColor: "bg-orange-500",
      path: `${INFLUENCER_BASE}/dashboard/generate-link`,
    },
    {
      title: "View Campaigns",
      description: "Manage your active campaigns",
      icon: "fas fa-briefcase",
      color: "text-green-600",
      bgColor: "bg-green-500",
      path: `${INFLUENCER_BASE}/dashboard/affiliate-dashboard`,
    },
    {
      title: "Check Earnings",
      description: "View your earnings and analytics",
      icon: "fas fa-dollar-sign",
      color: "text-purple-600",
      bgColor: "bg-purple-500",
      path: `${INFLUENCER_BASE}/dashboard/earnings`,
    },
  ];

  const upcomingFeatures = [
    {
      title: "AI Content Suggestions",
      description: "Get personalized content ideas for your campaigns.",
      icon: "fas fa-lightbulb",
      color: "text-yellow-600",
    },
    {
      title: "Advanced Analytics",
      description: "Deep insights into your performance metrics.",
      icon: "fas fa-chart-line",
      color: "text-indigo-600",
    },
  ];

  return (
    <div className="w-full p-4 lg:p-6">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Welcome */}
      <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 md:flex-row md:items-center md:justify-between lg:mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">
            Welcome back, {user?.fullName || user?.username || "Influencer"}!
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Ready to boost your earnings? Here's your performance overview and quick actions.
          </p>
        </div>
        <span className="hidden shrink-0 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white lg:inline-flex">
          Welcome to Your Dashboard
        </span>
      </div>

      {/* Quick Actions */}
      <div className="mb-4 lg:mb-6">
        <h2 className="mb-3 text-base font-bold text-gray-900 md:text-lg lg:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 sm:gap-3">
          {quickActions.map((action, index) => (
            <button
              key={index}
              type="button"
              onClick={() => navigate(action.path)}
              className="flex items-start gap-2.5 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-orange-200 hover:shadow-md sm:gap-3 sm:p-4"
            >
              <div className={`${action.bgColor} flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9`}>
                <i className={`${action.icon} text-white text-xs sm:text-sm`} />
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-semibold sm:text-sm ${action.color}`}>{action.title}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 sm:text-xs">{action.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      {loading || earningsLoading ? (
        <div className="mb-4 space-y-4 lg:mb-6 lg:space-y-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 h-5 w-40 animate-pulse rounded bg-gray-200" />
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                {[0, 1, 2, 3].map((j) => (
                  <div key={j} className="animate-pulse rounded-xl bg-gray-50 p-3 sm:p-4">
                    <div className="mb-2 h-3 w-16 rounded bg-gray-200" />
                    <div className="h-5 w-12 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-4 space-y-4 lg:mb-6 lg:space-y-6">
          {/* Affiliate Link Performance */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50">
                <i className="fas fa-link text-blue-600" />
              </div>
              <h2 className="text-sm font-bold text-gray-900 md:text-base lg:text-lg">Affiliate Link Performance</h2>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
              <StatCard label="Total Links" value={stats.totalLinks} tone="blue" />
              <StatCard label="Total Visits" value={stats.totalVisits.toLocaleString()} tone="purple" />
              <StatCard label="Unique Visits" value={stats.totalUniqueVisits.toLocaleString()} tone="indigo" />
              <StatCard label="Conversion Rate" value={`${stats.conversionRate}%`} tone="orange" />
            </div>
          </div>

          {/* Campaign Statistics */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-50">
                <i className="fas fa-briefcase text-green-600" />
              </div>
              <h2 className="text-sm font-bold text-gray-900 md:text-base lg:text-lg">Campaign Statistics</h2>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3">
              <StatCard label="Active Campaigns" value={stats.activeCampaigns} tone="green" icon="fas fa-check-circle" />
              <StatCard label="Pending Campaigns" value={stats.pendingCampaigns} tone="yellow" icon="fas fa-clock" />
              <StatCard label="Rejected Campaigns" value={stats.rejectedCampaigns} tone="red" icon="fas fa-times-circle" />
            </div>
          </div>

          {/* Earnings Overview */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50">
                  <i className="fas fa-dollar-sign text-emerald-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-900 md:text-base lg:text-lg">Earnings Overview</h2>
              </div>
              <div className="flex items-center gap-2">
                {isDataStale && (
                  <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-medium text-orange-600 sm:text-xs">
                    Data may be outdated
                  </span>
                )}
                <button
                  onClick={handleRefreshEarnings}
                  disabled={earningsLoading}
                  className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  title="Refresh earnings data"
                >
                  <i className={`fas fa-sync-alt text-sm ${earningsLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>

            {earningsError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-red-600" />
                  <p className="text-xs text-red-800 sm:text-sm">Error loading earnings: {earningsError}</p>
                </div>
                <button onClick={handleRefreshEarnings} className="mt-2 text-xs font-medium text-red-600 hover:text-red-800 sm:text-sm">
                  Try again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                <StatCard label="Total Earnings" value={`₹${stats.totalEarnings.toLocaleString("en-IN")}`} tone="emerald" icon="fas fa-wallet" />
                <StatCard label="Amount Paid" value={`₹${(earningsData?.amountPaid || 0).toLocaleString("en-IN")}`} tone="green" icon="fas fa-check-circle" />
                <StatCard label="Amount Due" value={`₹${stats.dueAmount.toLocaleString("en-IN")}`} tone="amber" icon="fas fa-hourglass-half" />
                <StatCard
                  label="Payment Status"
                  value={stats.dueAmount === 0 ? "Fully Paid" : (earningsData?.amountPaid || 0) > 0 ? "Partial" : "Unpaid"}
                  tone="blue"
                  icon={`fas ${stats.dueAmount === 0 ? "fa-check-circle" : (earningsData?.amountPaid || 0) > 0 ? "fa-clock" : "fa-exclamation-circle"}`}
                />
              </div>
            )}

            <div className="mt-3 sm:mt-4">
              <button
                onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/earnings`)}
                className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-200 sm:text-sm"
              >
                <i className="fas fa-chart-line" />
                View Detailed Earnings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Insights */}
      {!loading && !earningsLoading && (
        <AIInsights
          stats={{
            totalLinks: stats.totalLinks,
            totalVisits: stats.totalVisits,
            totalUniqueVisits: stats.totalUniqueVisits,
            conversionRate: stats.conversionRate,
            totalEarnings: stats.totalEarnings,
            activeCampaigns: stats.activeCampaigns,
          }}
          title="AI Performance Insights"
        />
      )}

      {/* Coming Soon */}
      <div className="mb-4 lg:mb-6">
        <h2 className="mb-3 text-base font-bold text-gray-900 md:text-lg lg:mb-4">Coming Soon</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
          {upcomingFeatures.map((feature, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                setUpcomingFeature(feature.title);
                setShowUpcomingModal(true);
              }}
              className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-orange-200 hover:shadow-md sm:p-5"
            >
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${feature.color.replace("text-", "bg-").replace("-600", "-50")}`}>
                <i className={`${feature.icon} ${feature.color} text-sm sm:text-base`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-semibold ${feature.color}`}>{feature.title}</p>
                  <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-medium text-orange-600">Coming Soon</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">{feature.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Upcoming Feature Modal */}
      {showUpcomingModal && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowUpcomingModal(false)}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-orange-50">
                <i className="fas fa-rocket text-2xl text-orange-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 sm:text-xl">{upcomingFeature}</h3>
              <p className="mt-2 text-sm text-gray-500">
                This exciting feature is currently in development and will be available soon!
                Stay tuned for updates.
              </p>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={() => setShowUpcomingModal(false)}
                  className="flex-1 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                  Got it!
                </button>
                <button
                  onClick={() => {
                    setShowUpcomingModal(false);
                    toast.info("We'll notify you when this feature is ready!");
                  }}
                  className="flex-1 rounded-xl bg-gray-100 px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                >
                  Notify Me
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AIAssistant context="dashboard" />
    </div>
  );
};

/* --------------------------------- Helpers -------------------------------- */

const TONE_STYLES = {
  blue: "border-blue-100 bg-blue-50 text-blue-700",
  purple: "border-purple-100 bg-purple-50 text-purple-700",
  indigo: "border-indigo-100 bg-indigo-50 text-indigo-700",
  orange: "border-orange-100 bg-orange-50 text-orange-700",
  green: "border-green-100 bg-green-50 text-green-700",
  yellow: "border-yellow-100 bg-yellow-50 text-yellow-700",
  red: "border-red-100 bg-red-50 text-red-700",
  emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
  amber: "border-amber-100 bg-amber-50 text-amber-700",
};

const StatCard = ({ label, value, tone = "blue", icon }) => (
  <div className={`min-w-0 rounded-xl border p-3 sm:p-4 ${TONE_STYLES[tone] || TONE_STYLES.blue}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-[11px] font-medium opacity-80 sm:text-xs">{label}</p>
        <p className="mt-0.5 truncate text-sm font-bold text-gray-900 sm:text-base lg:text-lg">{value}</p>
      </div>
      {icon && <i className={`${icon} shrink-0 text-sm opacity-60 sm:text-base`} />}
    </div>
  </div>
);

export default Dashboard;
